import { useMemo, useState, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import type { Table2DComponentSpec, Table2DDataSpec } from "../Processing/types";
import { buildTable2DGrid, heatmapCellColors } from "./table2dGrid";

// Custom HTML/CSS heatmap — replaces the Highcharts heatmap so platform view and
// PDF export share the same DOM/styling and we control layout (sticky headers on
// top + left, compact/labelled modes) directly.

interface TipState {
  /** header (row label), name (col label), value, and bullet color — mirrors Highcharts. */
  key: string;
  name: string;
  value: string;
  color: string;
  x: number;
  y: number;
}

export function Table2DHeatmap({
  table2d,
  spec,
  showDataLabels,
}: {
  table2d: Table2DDataSpec;
  spec: Table2DComponentSpec;
  showDataLabels: boolean;
}) {
  const grid = useMemo(() => buildTable2DGrid(table2d, spec), [table2d, spec]);
  const [tip, setTip] = useState<TipState | null>(null);

  if (grid.rows.length === 0 || grid.cols.length === 0) return null;

  // Single delegated tooltip — avoids one component per cell on large grids.
  const handleMove = (e: MouseEvent<HTMLDivElement>) => {
    const cell = (e.target as HTMLElement).closest<HTMLElement>(".t2d-heatmap__cell");
    if (!cell || cell.dataset.tipName === undefined) {
      if (tip) setTip(null);
      return;
    }
    setTip({
      key: cell.dataset.tipKey ?? "",
      name: cell.dataset.tipName,
      value: cell.dataset.tipValue ?? "",
      color: cell.dataset.tipColor || "#bfbfbf",
      x: e.clientX,
      y: e.clientY,
    });
  };

  return (
    <div className="t2d-heatmap-scroll" onMouseMove={handleMove} onMouseLeave={() => setTip(null)}>
      <table className={`t2d-heatmap${showDataLabels ? "" : " t2d-heatmap--compact"}`}>
        <thead>
          <tr>
            <th className="t2d-heatmap__corner" />
            {grid.cols.map((c) => (
              <th key={c.key} className={`t2d-heatmap__colhead${c.pinned ? " t2d-heatmap__pinned" : ""}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.rows.map((row) => (
            <tr key={row.key}>
              <th className={`t2d-heatmap__rowhead${row.pinned ? " t2d-heatmap__pinned" : ""}`}>
                {row.label}
              </th>
              {row.cells.map((cell, ci) => {
                const isPinnedCell = row.pinned || grid.cols[ci].pinned;
                const { bg, color } = heatmapCellColors(cell.value, cell.hex, isPinnedCell);
                return (
                  <td
                    key={grid.cols[ci].key}
                    className="t2d-heatmap__cell"
                    style={{ background: bg, color }}
                    data-tip-key={row.label}
                    data-tip-name={grid.cols[ci].label}
                    data-tip-value={cell.label}
                    data-tip-color={cell.hex ?? ""}
                  >
                    {showDataLabels && cell.value != null ? cell.label : ""}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {tip &&
        createPortal(
          <div className="t2d-heatmap__tooltip" style={{ left: tip.x + 12, top: tip.y + 12 }}>
            <span className="t2d-heatmap__tooltip-key">{tip.key}</span>
            <div>
              <span className="t2d-heatmap__tooltip-dot" style={{ color: tip.color }}>●</span>
              {tip.name}: <b>{tip.value}</b>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
